import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSliders, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { UserPreferences, useUserPreferences } from '../../hooks/useUserPreferences';

/**
 * The account's settings. Renders nothing for a guest, who has no account to
 * keep them on.
 */
const PreferencesSection: React.FC = () => {
    const { t } = useTranslation();
    const { preferences, canEdit, updatePreference } = useUserPreferences();
    const [saving, setSaving] = useState<keyof UserPreferences | null>(null);
    const [error, setError] = useState('');

    if (!canEdit) return null;

    const toggle = async (key: keyof UserPreferences) => {
        setSaving(key);
        setError('');
        try {
            await updatePreference(key, !preferences[key]);
        } catch (err) {
            console.error('Error saving preference:', err);
            setError(t('profile.components.preferencesSection.saveFailed'));
        } finally {
            setSaving(null);
        }
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1 flex items-center">
                <FontAwesomeIcon icon={faSliders} className="mr-2" aria-hidden="true" />
                {t('profile.components.preferencesSection.title')}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                {t('profile.components.preferencesSection.subtitle')}
            </p>

            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <p id="pref-split-first-label" className="text-sm font-medium text-gray-900 dark:text-white">
                        {t('profile.components.preferencesSection.splitFirst.label')}
                    </p>
                    <p id="pref-split-first-description" className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                        {t('profile.components.preferencesSection.splitFirst.description')}
                    </p>
                </div>
                <button
                    type="button"
                    role="switch"
                    aria-checked={preferences.splitFirst}
                    aria-labelledby="pref-split-first-label"
                    aria-describedby="pref-split-first-description"
                    onClick={() => toggle('splitFirst')}
                    disabled={saving !== null}
                    className={`relative inline-flex flex-shrink-0 items-center w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 disabled:opacity-60 ${
                        preferences.splitFirst ? 'bg-indigo-500' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                >
                    <span
                        className={`inline-flex items-center justify-center w-5 h-5 rounded-full bg-white shadow transform transition-transform duration-200 ${
                            preferences.splitFirst ? 'translate-x-5' : 'translate-x-0.5'
                        }`}
                    >
                        {saving === 'splitFirst' && (
                            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-[10px] text-gray-400" aria-hidden="true" />
                        )}
                    </span>
                </button>
            </div>

            {error && (
                <p className="mt-3 text-sm text-red-600 dark:text-red-400" role="alert">
                    {error}
                </p>
            )}
        </div>
    );
};

export default PreferencesSection;
